import { ApiProvider } from 'Core'
import useAppView from 'Core/Hooks/useAppView'
import { getUserInfo } from 'Utils/Helpers'
import _ from 'lodash'
import { useCallback, useRef, useState } from 'react'

/**
 * @param {{
 * indexes: string[]
 * startDateTime: Date,
 * endDateTime: Date,
 * deviceIdentifier?: string
 * resourceIdentifier?: string
 * userName?: string
 * }} param0
 * @returns
 */
const useMultiQuery = ({
  indexes,
  userName,
  startDateTime,
  endDateTime,
  deviceIdentifier,
  resourceIdentifier
}) => {
  const prevHitsRef = useRef({})
  const abortControllerRef = useRef(new AbortController())
  const [fetching, setFetching] = useState(false)
  const [backgroundFetching, setBackgroundFetching] = useState(false)
  const [hits, setHits] = useState([])
  const { appView } = useAppView()

  const createAbortController = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort() // Abort the previous fetch (if any)
    }
    abortControllerRef.current = new AbortController() // Create a new AbortController
    return abortControllerRef.current.signal // Get the new AbortSignal
  }, [abortControllerRef])

  const fetchDataWithCancellation = useCallback(async (abortSignal, from, batchSize) => {
    const user = getUserInfo()
    const UserName = appView === 'user' ? user.Spec.EmailID : userName || ''

    try {
      const res = await new ApiProvider('elasticlog')
        .setInstance({
          Indices: {
            Elems: indexes
          },
          Filters: {
            UserName: UserName,
            DeviceName: deviceIdentifier || '',
            ResourceName: resourceIdentifier || ''
          },
          Size: batchSize,
          StartTime: startDateTime,
          EndTime: endDateTime,
          From: from,
          Attributes: {
            Map: {
              track_total_hits: 'true'
            }
          }
        })
        .post()

      if (abortSignal && abortSignal.aborted) {
        throw new Error('fetch was cancelled')
      }

      return res
    } catch (error) {
      if (abortSignal && abortSignal.aborted) throw error
    }
  }, [appView, indexes, userName, deviceIdentifier, resourceIdentifier, startDateTime, endDateTime])

  const getQueryLogs = useCallback(async () => {
    const abortSignal = createAbortController()
    setFetching(true)
    setBackgroundFetching(false)
    let from = 0
    let allHits = []
    const initialBatchSize = 100
    const backgroundBatchSize = 500
    const ELASTIC_LIMIT = 100000
    let totalRecords

    try {
      // 1. Initial Fetch
      const response = await fetchDataWithCancellation(abortSignal, from, initialBatchSize)
      const decodeResponse = response?.data ? JSON.parse(atob(response?.data?.Response)) : ''
      const firstHits = decodeResponse?.hits?.hits || []
      totalRecords = decodeResponse?.hits?.total?.value
      allHits = [...firstHits]
      setHits(allHits)
      setFetching(false)

      // 2. Background Fetching
      if (allHits.length < totalRecords && allHits.length < ELASTIC_LIMIT) {
        setBackgroundFetching(true)
        from += initialBatchSize

        while (true) {
          if (abortSignal.aborted) break

          // Safety check: ensure from + size <= ELASTIC_LIMIT
          const currentBatchSize = Math.min(backgroundBatchSize, ELASTIC_LIMIT - from)
          if (currentBatchSize <= 0) break

          const response = await fetchDataWithCancellation(abortSignal, from, currentBatchSize)
          const decodeResponse = response?.data ? JSON.parse(atob(response?.data?.Response)) : ''
          const nextHits = decodeResponse?.hits?.hits || []

          if (nextHits.length === 0) break

          allHits = [...allHits, ...nextHits]
          setHits([...allHits])

          from += currentBatchSize
          if (allHits.length >= totalRecords || from >= ELASTIC_LIMIT) break
        }
      }
      prevHitsRef.current = allHits
    } catch (error) {
      if (error.message !== 'fetch was cancelled') {
        console.error(error)
      }
    } finally {
      setFetching(false)
      setBackgroundFetching(false)
    }
  }, [
    indexes,
    userName,
    startDateTime,
    endDateTime,
    deviceIdentifier,
    resourceIdentifier,
    appView,
    createAbortController,
    fetchDataWithCancellation,
    prevHitsRef,
    setFetching,
    setHits
  ])

  return {
    getQueryLogs,
    fetching,
    backgroundFetching,
    hits
  }
}

export { useMultiQuery }
